import { useState, useEffect, useContext, useMemo } from 'react'
import type { ReactElement } from 'react'
import moment from 'moment'
import { Base64 } from 'js-base64'
import { useTranslation } from 'react-i18next'
import { CachePolicies, useFetch } from 'use-http'
import {
  Box,
  Flex,
  Text,
  Tag,
  TagLabel,
  TagLeftIcon,
  VStack,
  Divider,
  Center,
  Icon,
  Spinner,
  Link,
  HStack,
  Avatar,
  Tooltip,
  Button,
} from '@chakra-ui/react'
import SearchBar from '../../SearchBar'
import { CopyIcon } from '../../CopyIcon'

import { MdFolder } from 'react-icons/md'
import { AiOutlineTag } from 'react-icons/ai'
import {
  IoImages,
  IoLockClosed,
  IoWarning,
  IoInformationCircleSharp,
} from 'react-icons/io5'
import { BsArrowRight, BsArrowDown } from 'react-icons/bs'
import { FaUsers } from 'react-icons/fa'
import AWSIcon from '../../../../graphics/AWSIcon'
import GoogleIcon from '../../../../graphics/GoogleIcon'

import { UserContext } from '../../../providers'
import useToast from '../../../hooks/useToast'
import { FeaturesEnum } from '../../../../utils/features'
import { PermissionDescriptionMap } from '../../../../utils/permissions'
import type {
  MediaAuditLog,
  MediaAuditLogsRequest,
  AuditLogsErrorResponse,
  FileDetailsResponse,
  FolderDetailsResponse,
  MediaCollectionDetailsResponse,
  UserSessionResponse,
  HistoryAuditLogsResponse,
  NestedAssetRename
} from '../../../interfaces'
import { colors } from '../../../../ui-kit'
import { removeLeadingSlash, removeTrailingSlash } from '../../../../utils/urlHelpers'

interface CreationLog
  extends Pick<
    MediaAuditLog,
    'id' | 'userId' | 'userName' | 'userEmail' | 'details'
  > {
  timestamp: number | null
  creationEvent: true
  operation: 'file.created' | 'folder.created' | 'media-collection.created'
}

type UnifiedLog =
  | (MediaAuditLog & { searchString: string })
  | (CreationLog & { searchString: string })
  | (NestedAssetRename & { searchString: string })

const stringify = (data): string => {
  switch (true) {
    case !Boolean(data):
      return ''
    case typeof data === 'string':
      return data
    case Array.isArray(data):
      return data.length
        ? data
            .map(stringify)
            .filter(x => x)
            .join(' ')
        : ''
    case typeof data === 'object':
      return Object.keys(data).length
        ? Object.entries(data)
            .map(([k, v]) => `${k} ${stringify(v)}`)
            .join(' ')
        : ''
    default:
      return JSON.stringify(data)
  }
}

// Helper to format custom metadata values for display
const formatMetadataValue = (data: unknown): string => {
  switch (true) {
    case data === false:
      return 'false'
    case !Boolean(data):
      return 'None'
    case Array.isArray(data):
      return (data as unknown[]).map(formatMetadataValue).join(', ') || 'None'
    case typeof data === 'string' &&
      moment(data, moment.ISO_8601, true).isValid():
      return moment(data as string).format('DD[/]MM[/]YYYY')
    default:
      return String(data)
  }
}

const addSearchString = (log: MediaAuditLog | CreationLog | NestedAssetRename): UnifiedLog => {
  const { userName, userEmail, timestamp } = log
  const { prevState = {}, newState = {}, info = {} } = log.details || {}

  const additionalValues = {
    customCoordinate:
      log.operation === 'file.update' &&
      (prevState.customCoordinates || newState.customCoordinates) &&
      'Custom Coordinates Height Width X and Y coordinates',
    customMetadata:
      log.operation.includes('update') &&
      (prevState.customMetadata || newState.customMetadata) &&
      'Custom metadata',
    acl:
      log.operation === 'change_access_control' &&
      (info.length
        ? info.map(u => PermissionDescriptionMap[u.permission]?.label)
        : 'Removed access for all restricted users & user groups'),
    publicLink:
      log.operation.includes('public_link') &&
      (info.password ? 'Protected' : '') +
        ((info.validFrom || info.validTill) &&
          'Valid' +
            (info.validFrom
              ? ` From ${moment(info.validFrom).format('MMMM D, YYYY')}`
              : '') +
            (info.validTill
              ? ` Till ${moment(info.validTill).format('MMMM D, YYYY')}`
              : '')),
  }
  return {
    ...log,
    searchString: stringify(
      Object.values({
        userName,
        userEmail,
        operation: getLogLabel(log),
        prevState:
          typeof prevState === 'object' ? Object.values(prevState) : prevState,
        newState:
          typeof newState === 'object' ? Object.values(newState) : newState,
        info: typeof info === 'object' ? Object.values(info) : info,
        timestamp: timestamp
          ? moment(timestamp).format('MMMM D, YYYY [at] hh:mm a')
          : '',
        ...additionalValues,
      }).filter(x => x),
    ),
  }
}

const AuditLogs = ({
  entityId,
  entityDetails,
}: {
  entityId: string
  entityDetails:
    | FileDetailsResponse
    | FolderDetailsResponse
    | MediaCollectionDetailsResponse
}): ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const getCreationLog = (
    origin: HistoryAuditLogsResponse['origin'],
  ): UnifiedLog => {
    const movedOrCopied = ['Move', 'Copy'].includes(origin?.reason as string)
    const user = (movedOrCopied ? origin?.by : entityDetails?.createdBy) as
      | {
          userId: string
          email: string
          name: string
        }
      | undefined
    return addSearchString({
      id: 'created',
      creationEvent: true,
      userId: user?.userId,
      userEmail: user?.email,
      userName: user?.name || 'API User',
      timestamp: entityDetails.createdAt
        ? new Date(entityDetails.createdAt).getTime()
        : null,
      operation: entityDetails['fileId']
        ? 'file.created'
        : entityDetails['folderId']
        ? 'folder.created'
        : 'media-collection.created',
      details: {
        info: origin,
      },
    })
  }

  const { user } = useContext(UserContext)
  const featureEnabled =
    user?.clientNumber &&
    user.pricingPlan.featuresList.includes(FeaturesEnum.CENTRAL_AUDIT_LOGS)

  const { request, loading = false } = useFetch<MediaAuditLogsRequest>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [logs, setLogs] = useState<UnifiedLog[]>([])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [visibleLogs, setVisibleLogs] = useState<UnifiedLog[]>([])

  const getLogs = async (): Promise<void> => {
    if (featureEnabled && entityId) {
      const response = (await request.get(
        `/api/clients/${user.clientNumber}/media-audit-logs/history/${entityId}`,
      )) as unknown as HistoryAuditLogsResponse | AuditLogsErrorResponse

      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }

      const newLogs = (response as HistoryAuditLogsResponse)?.logs?.map(
        addSearchString,
      )

      const creationLog = getCreationLog(
        (response as HistoryAuditLogsResponse).origin,
      )

      setLogs(newLogs.concat(creationLog))
    }
  }

  useEffect(() => {
    void getLogs()
  }, [user, entityId])

  useEffect(() => {
    setVisibleLogs(
      searchQuery
        ? logs.filter(log =>
            log.searchString.toLowerCase().includes(searchQuery.toLowerCase()),
          )
        : logs,
    )
  }, [logs, searchQuery])

  return (
    <Flex direction="column" position="relative" width="full">
      {loading ? (
        <Center height="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="xl"
          />
        </Center>
      ) : (
        <Box>
          <VStack divider={<Divider />} alignItems="flex-start">
            <Box
              alignSelf="center"
              width="100%"
              padding="0rem 1.2rem"
              marginTop="0.5rem"
            >
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                minWidth="100%"
              />
            </Box>

            {visibleLogs.length || entityDetails ? (
              <VStack divider={<Divider />} spacing="0" width="100%">
                {visibleLogs.map(log => (
                  <Event key={log.id} log={log} user={user} />
                ))}
              </VStack>
            ) : (
              <Text>No data available</Text>
            )}
          </VStack>
        </Box>
      )}
    </Flex>
  )
}

function Event({
  log,
  user,
}: {
  log: MediaAuditLog | CreationLog | NestedAssetRename
  user: UserSessionResponse | null
}): ReactElement {
  const { timestamp } = log
  const userNameToDisplay =
    user && (user.id === log.userId || user.userEmail === log.userEmail)
      ? 'You'
      : log.userName || 'API User'

  const logLabel = useMemo(() => {
    return getLogLabel(log)
  }, [log])

  return (
    <Flex width="100%" padding="1rem">
      <Avatar
        name={log.userName || 'API User'}
        getInitials={name => name.split(' ', 1)[0].charAt(0)}
      />
      <Flex margin="0rem 1rem" direction="column" flex="1">
        <Flex marginBottom="0.5rem" direction="column">
          <Box>
            <Text as="span" fontWeight="600" fontSize="1rem">
              {userNameToDisplay}
            </Text>
            <Text as="span"> {logLabel}</Text>
          </Box>
          <Text color={colors.appSlate} fontSize="0.75rem">
            {timestamp
              ? moment(timestamp).format('MMM D, YYYY [at] hh:mm a')
              : 'Unknown'}
          </Text>
        </Flex>
        <EventContent log={log} />
      </Flex>
    </Flex>
  )
}

function EventContent({
  log,
}: {
  log: Pick<MediaAuditLog | CreationLog | NestedAssetRename, 'operation' | 'details'> 
}): ReactElement {
  const { prevState, newState, info } = log.details || {}
  switch (log.operation) {
    case 'file.update':
    case 'folder.update':
    case 'media-collection.update':
      const hasCustomMetadata =
        prevState.customMetadata || newState.customMetadata
      const hasCustomCoordinates =
        prevState.customCoordinates || newState.customCoordinates
      const hasDescription = prevState.description || newState.description
      if (!hasCustomMetadata && !hasCustomCoordinates && !hasDescription) {
        return <></>
      }

      const [px, py, pw, ph] = prevState.customCoordinates?.split(',', 4) || []
      const [nx, ny, nw, nh] = newState.customCoordinates?.split(',', 4) || []

      const prevCm = prevState.customMetadata
      const newCm = newState.customMetadata

      const allKeys: string[] = hasCustomMetadata
        ? Array.from(
            new Set(
              prevCm.map(({ key }) => key).concat(newCm.map(({ key }) => key)),
            ),
          )
        : []

      const cmChangeList = allKeys
        .map(key => ({
          key,
          prevState: formatMetadataValue(prevCm?.find(cm => cm.key === key)?.value),
          newState: formatMetadataValue(newCm?.find(cm => cm.key === key)?.value),
        }))
        .filter(({ prevState, newState }) => prevState !== newState)

      return (
        <Flex direction="column" gap="0.5rem">
          {hasCustomMetadata ? (
            <>
              <Text fontSize="0.85rem" fontWeight="600">
                Custom Metadata
              </Text>
              {cmChangeList.map(({ key, prevState, newState }) => {
                return (
                  <Box key={key}>
                    <Text color={colors.appSlate} fontSize="0.85rem">
                      {key}
                    </Text>
                    <Flex alignItems="center" gap="0.25rem">
                      <Text>{prevState}</Text>
                      <Icon as={BsArrowRight} />
                      <Text>{newState}</Text>
                    </Flex>
                  </Box>
                )
              })}
            </>
          ) : (
            <></>
          )}
          {hasCustomCoordinates ? (
            <>
              <Text fontSize="0.8rem" fontWeight="600">
                Custom Coordinates
              </Text>
              <Box>
                {px === nx && py === ny ? (
                  <></>
                ) : (
                  <>
                    <Text color={colors.appSlate} fontSize="0.75rem">
                      X and Y coordinates
                    </Text>
                    <Flex alignItems="center" gap="0.25rem">
                      <Text>{px && py ? `${px},${py}` : 'None'}</Text>
                      <Icon as={BsArrowRight} />
                      <Text>{nx && ny ? `${nx},${ny}` : 'None'}</Text>
                    </Flex>
                  </>
                )}
                {pw === nw && ph === nh ? (
                  <></>
                ) : (
                  <>
                    <Text color={colors.appSlate} fontSize="0.75rem">
                      Width and Height
                    </Text>
                    <Flex alignItems="center" gap="0.25rem">
                      <Text>{pw && ph ? `${pw} x ${ph}` : 'None'}</Text>
                      <Icon as={BsArrowRight} />
                      <Text>{nw && nh ? `${nw} x ${nh}` : 'None'}</Text>
                    </Flex>
                  </>
                )}
              </Box>
            </>
          ) : (
            <></>
          )}
          {hasDescription ? (
            <>
              <Text fontSize="0.8rem" fontWeight="600">
                Description
              </Text>
              <DescriptionChange prevState={prevState} newState={newState} />
            </>
          ) : (
            <></>
          )}
        </Flex>
      )
    case 'file.add_ai_tags':
      return (
        <TagChange isAITags={true} prevState={prevState} newState={newState} />
      )
    case 'file.add_tags':
    case 'file.remove_tags':
    case 'file.remove_ai_tags':
      return (
        <TagChange
          isAITags={log.operation === 'file.remove_ai_tags'}
          prevState={prevState}
          newState={newState}
        />
      )
    case 'file.rename':
    case 'media-collection.rename':
    case 'folder.rename':
      return renderSimpleRenameUI(prevState, newState)
    case 'nested_asset_rename': {
      const prevPath = removeTrailingSlash(removeLeadingSlash(prevState)).split('/')
      const nextPath = removeTrailingSlash(removeLeadingSlash(newState)).split('/')
      let changedIndex = -1
      nextPath.find((path, ind) => {
        if (prevPath[ind] !== path) {
          changedIndex = ind
          return true
        }
        return false
      })
      if (changedIndex === -1 || (prevPath.length !== nextPath.length)) {
       renderSimpleRenameUI(prevState, newState)
      }
      return (
        <VStack wordBreak="break-all" alignItems="center">
          {renderColoredPath(prevPath, changedIndex, colors.appLightRed)}
          <Icon as={BsArrowDown} />
          {renderColoredPath(nextPath, changedIndex, colors.appLightGreen)}
        </VStack>
      )
    }
    case 'file.create_version':
      return (
        <Flex alignItems="center" color={colors.appBlue} gap="0.25rem">
          <Icon as={IoInformationCircleSharp} boxSize="14px" />
          <Text fontSize="0.8rem" alignSelf="flex-end">
            Due to {info.reason || 'Upload'} operation
          </Text>
        </Flex>
      )
    case 'add_public_link':
    case 'update_public_link':
    case 'delete_public_link':
      return (
        <VStack alignItems="flex-start">
          <HStack
            padding="0.5rem"
            border="1px"
            borderColor={colors.appPaleLilac}
            borderRadius="0.25rem"
          >
            <Text
              width="25ch"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {info.publicURL}
            </Text>
            <CopyIcon textToCopy={info.publicURL} />
          </HStack>
          <Box marginTop="0.25rem" fontSize="0.75rem">
            <HStack divider={<Box border="0">•</Box>} color={colors.appSlate}>
              {info.password && (
                <Flex alignItems="center">
                  <Icon as={IoLockClosed} />
                  <Text as="span" marginLeft="0.5ch">
                    Protected
                  </Text>
                </Flex>
              )}
              {(info.validFrom || info.validTill) && (
                <Text>
                  {'Valid' +
                    (info.validFrom
                      ? ` from ${moment(info.validFrom?.$date).format('MMM D, YYYY')}`
                      : '') +
                    (info.validTill
                      ? ` till ${moment(info.validTill?.$date).format('MMM D, YYYY')}`
                      : '')}
                </Text>
              )}
            </HStack>
            {info.exposeDraftAssets && (
              <Box
                marginTop="0.5rem"
                width="auto"
                padding="1px 2px"
                display="inline-block"
                border="1px"
                borderColor={colors.appPaleLilac}
                borderRadius="0.25rem"
                backgroundColor={colors.appPaleGrey}
              >
                Unpublished assets exposed
              </Box>
            )}
          </Box>
        </VStack>
      )
    case 'media-collection.add_assets':
    case 'media-collection.remove_assets':
      return (
        <VStack alignItems="flex-start" gap="0.25rem">
          <ExpandableList
            itemList={info.map(asset => (
              <Link
                href={
                  asset.type === 'file'
                    ? `/dashboard/media-library/detail/${asset.id}`
                    : `/dashboard/media-library/${Base64.encode(
                        asset.path,
                        true,
                      )}`
                }
                key={asset.path}
                textDecoration="none"
                _hover={{ textDecoration: 'none' }}
              >
                <Flex
                  alignItems="center"
                  padding="0.2rem"
                  border="1px"
                  borderColor={colors.appPaleLilac}
                  borderRadius="0.25rem"
                >
                  <Icon
                    as={asset.type === 'file' ? IoImages : MdFolder}
                    margin="0 0.5rem"
                    color={colors.appGreyTwo}
                  />
                  <Text
                    width="25ch"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    color="#1a202c"
                    fontWeight="normal"
                  >
                    {asset.path.slice(1)}
                  </Text>
                </Flex>
              </Link>
            ))}
          />
        </VStack>
      )
    case 'change_access_control':
      return info.length !== 0 ? (
        <VStack alignItems="flex-start" gap="0.4rem">
          <ExpandableList
            itemList={info.map(({ type, permission, name }) => (
              <Flex alignItems="flex-start" key={name + type + permission}>
                <Avatar
                  size="xs"
                  name={type === 'USER' ? name : ''}
                  icon={<FaUsers fontSize="1.2rem" color={colors.appSlate} />}
                  margin="0.2rem 0.5rem"
                />
                <Flex direction="column">
                  <Text fontWeight="500">{name}</Text>
                  <Text color={colors.appSlate} fontSize="0.8rem">
                    {PermissionDescriptionMap[permission].label}
                  </Text>
                </Flex>
              </Flex>
            ))}
          />
        </VStack>
      ) : (
        <Text>Removed access for all restricted users & user groups</Text>
      )
    case 'file.created':
    case 'folder.created':
      const origin = info as HistoryAuditLogsResponse['origin']
      if (origin?.path && origin?.id) {
        return (
          <Flex direction="column" alignItems="flex-start" gap="0.5rem">
            <Text>
              <Text as="span" fontWeight="600">
                Previous Path :
              </Text>{' '}
              {origin.path}
            </Text>
          </Flex>
        )
      }
      return <></>
    case 'file.apply_extension':
      if (info.service === 'ai-auto-description') {
        return <DescriptionChange prevState={prevState} newState={newState} />
      } else if (info.service === 'ai-tasks') {
        // AI tasks can modify both tags and custom metadata in a single operation
        const prevTags = prevState?.tags || []
        const newTags = newState?.tags || []
        const prevCustomMetadata = prevState?.customMetadata || {}
        const newCustomMetadata = newState?.customMetadata || {}
        
        // Order-insensitive comparison helper
        const arraysEqual = (arr1: any[], arr2: any[]): boolean => {
          if (arr1.length !== arr2.length) return false
          const sorted1 = [...arr1].sort()
          const sorted2 = [...arr2].sort()
          return JSON.stringify(sorted1) === JSON.stringify(sorted2)
        }
        
        const hasTagChanges = !arraysEqual(prevTags, newTags)
        const hasMetadataChanges = JSON.stringify(prevCustomMetadata) !== JSON.stringify(newCustomMetadata)
        
        if (!hasTagChanges && !hasMetadataChanges) {
          return <></>
        }

        return (
          <Flex direction="column" gap="0.75rem">
            {hasTagChanges && (
              <>
                <Text fontSize="0.8rem" fontWeight="600">
                  Tags
                </Text>
                <TagChange isAITags={false} prevState={prevTags} newState={newTags} />
              </>
            )}
            {hasMetadataChanges && (
              <>
                <Text fontSize="0.8rem" fontWeight="600">
                  Custom Metadata
                </Text>
                <VStack alignItems="flex-start" gap="0.5rem">
                  {Object.keys({ ...prevCustomMetadata, ...newCustomMetadata }).map(key => {
                    const prevValue = prevCustomMetadata[key]
                    const newValue = newCustomMetadata[key]
                    
                    // Skip if values are the same (order-insensitive for arrays)
                    const valuesEqual = Array.isArray(prevValue) && Array.isArray(newValue)
                      ? arraysEqual(prevValue, newValue)
                      : JSON.stringify(prevValue) === JSON.stringify(newValue)
                    
                    if (valuesEqual) return null
                    
                    return (
                      <Box key={key}>
                        <Text color={colors.appSlate} fontSize="0.85rem">
                          {key}
                        </Text>
                        <Flex alignItems="center" gap="0.25rem">
                          <Text>{formatMetadataValue(prevValue)}</Text>
                          <Icon as={BsArrowRight} />
                          <Text>{formatMetadataValue(newValue)}</Text>
                        </Flex>
                      </Box>
                    )
                  })}
                </VStack>
              </>
            )}
            {info.skippedFields && info.skippedFields.length > 0 && (
              <>
                <Text fontSize="0.8rem" fontWeight="600" color={colors.appYellowOrange}>
                  Skipped Operations
                </Text>
                <VStack alignItems="flex-start" gap="0.5rem">
                  {info.skippedFields.map((field, index) => (
                    <Box key={index} fontSize="0.85rem">
                      <Text>
                        <Text as="span" fontWeight="600">{field.field}</Text>
                        <Text as="span" color={colors.appSlate}> ({field.operation})</Text>
                      </Text>
                      <Text color={colors.appSlate}>{field.reason}</Text>
                      {field.operation === 'set' && field.attemptedValue !== undefined && (
                        <Text color={colors.appSlate}>
                          Attempted: <Text as="span" color="#1a202c">{formatMetadataValue(field.attemptedValue)}</Text>
                        </Text>
                      )}
                    </Box>
                  ))}
                </VStack>
              </>
            )}
          </Flex>
        )
      } else {
        return <></>
      }
    default:
      return <></>
  }
}

const renderSimpleRenameUI = (prevState, newState) => {
  return (<VStack wordBreak="break-all" alignItems="center">
    <Text>{prevState}</Text>
    <Icon as={BsArrowDown} />
    <Text>{newState}</Text>
  </VStack>)
}
const renderColoredPath = (pathArray, changedIndex, color) => (
  <Text as="span">
    {'/'}
    {pathArray.map((segment, i) => (
      <Text
        as="span"
        key={i}>
        <Text as="span"
          backgroundColor={i === changedIndex ? `${color}` : 'inherit'}
        >
          {segment}
        </Text >
        {i < pathArray.length - 1 ? '/' : ''}
      </Text>
    ))}
  </Text>
)

export default AuditLogs

const ExpandableList = ({
  itemList,
  initialLength = 3,
}: {
  itemList: ReactElement[]
  initialLength?: number
}): ReactElement => {
  const { t } = useTranslation()
  const [moreItemsVisible, toggleMoreItems] = useState(false)

  const initialItems: ReactElement[] = itemList.slice(0, initialLength)
  const moreItems: ReactElement[] = itemList.slice(initialLength)

  return initialItems.length > 0 ? (
    <>
      {initialItems}
      {moreItemsVisible && moreItems.length ? (
        <>
          {moreItems}
          <Text
            textAlign="right"
            cursor="pointer"
            color={colors.appBlue}
            onClick={() => void toggleMoreItems(false)}
          >
            {t('see_less', 'See less')}
          </Text>
        </>
      ) : null}
      {!moreItemsVisible && moreItems.length ? (
        <Text
          textAlign="right"
          cursor="pointer"
          color={colors.appClearBlue}
          onClick={() => void toggleMoreItems(true)}
        >
          {t('see_all', 'See all')}
        </Text>
      ) : null}
    </>
  ) : (
    <></>
  )
}

function ExpandableText({ text }: { text: string }): ReactElement {
  const [expanded, setExpanded] = useState(false)

  const textToDisplay = useMemo(() => {
    if (expanded) {
      return text
    } else if (text.length > 100) {
      return `${text.slice(0, 100)}...`
    }
    return text
  }, [expanded, text])

  return (
    <Box>
      <Text wordBreak="break-word">{textToDisplay}</Text>
      {text.length > 100 && (
        <Button
          size="sm"
          variant="link"
          color="appClearBlue"
          mt={1}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'See less' : 'See more'}
        </Button>
      )}
    </Box>
  )
}

const difference = (set1: Set<string>, set2: Set<string>): Set<string> => {
  const result: Set<string> = new Set()
  for (const v of set1) {
    if (!set2.has(v)) {
      result.add(v)
    }
  }
  return result
}

function TagChange({ isAITags, prevState, newState }) {
  const prevSet = new Set(
    prevState.map(x => (isAITags ? x.name + x.source : x)),
  ) as Set<string>
  const newSet = new Set(
    newState.map(x => (isAITags ? x.name + x.source : x)),
  ) as Set<string>

  // Check if there's any actual content change, not just length
  const showChange = prevSet.size !== newSet.size || 
    Array.from(prevSet).some(item => !newSet.has(item)) ||
    Array.from(newSet).some(item => !prevSet.has(item))

  // Calculate added and removed tags separately
  const addedTags = difference(newSet, prevSet)  // In new but not in prev
  const removedTags = difference(prevSet, newSet)  // In prev but not in new

  return showChange ? (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      gap="0.25rem"
    >
      <TagList
        tags={prevState}
        isAITags={isAITags}
        diff={removedTags}
        addedTags={false}
      />
      <Icon as={BsArrowRight} />
      <TagList
        tags={newState}
        isAITags={isAITags}
        diff={addedTags}
        addedTags={true}
      />
    </Flex>
  ) : (
    <>
      <Flex
        alignItems="center"
        color={colors.appRed}
        gap="0.25rem"
        marginBottom="0.5rem"
      >
        <Icon as={IoWarning} boxSize="14px" />
        <Text fontSize="0.8rem" alignSelf="flex-end">
          No new AI tags were added
        </Text>
      </Flex>
      {newState?.length ? (
        <TagList tags={newState} isAITags={isAITags} />
      ) : (
        <></>
      )}
    </>
  )
}

function TagList({
  tags,
  isAITags,
  diff = null,
  addedTags = false,
}: {
  tags: any
  isAITags: boolean
  diff?: Set<string> | null
  addedTags?: boolean
}): ReactElement {
  if (Array.isArray(tags) && tags.length) {
    const bgColor = diff && diff.size > 0 && (addedTags ? '#ECFDF0' : '#FBE9EB')
    return (
      <Flex flexWrap="wrap" gap="0.25rem">
        {tags.map(tag => {
          const name = isAITags ? tag.name : tag
          return (
            <DisplayTag
              key={name + (isAITags ? tag.source : '')}
              name={name}
              bgColor={
                diff?.has(isAITags ? name + tag.source : name)
                  ? (bgColor as string)
                  : 'transparent'
              }
              icon={
                isAITags
                  ? tag.source === 'google-auto-tagging'
                    ? GoogleIcon
                    : AWSIcon
                  : AiOutlineTag
              }
            />
          )
        })}
      </Flex>
    )
  }
  return <Text>None</Text>
}

function DisplayTag({ name, bgColor, icon }) {
  return (
    <Tag variant="outline" padding="0.3rem" backgroundColor={bgColor}>
      <TagLeftIcon as={icon} color="#1A202C" boxSize="1.25rem" />
      <TagLabel color="#1A202C" maxWidth="13ch">
        {name}
      </TagLabel>
    </Tag>
  )
}

function DescriptionChange({ prevState, newState }) {
  return (
    <>
      <Flex alignItems="center" gap="0.25rem" flexDirection="column">
        <ExpandableText text={prevState.description || 'None'} />
        <Icon as={BsArrowDown} />
        <ExpandableText text={newState.description || 'None'} />
      </Flex>
    </>
  )
}

function getLogLabel({
  operation,
  details = {},
}:
  Pick<MediaAuditLog | CreationLog | NestedAssetRename, 'operation' | 'details'>)
  : string {
  const { info } = details
  switch (operation) {
    case 'file.update':
    case 'folder.update':
      return 'updated the details'
    case 'file.apply_extension':
      if (info.service === 'remove-bg') {
        return 'applied extension Remove Background'
      } else if (info.service === 'ai-auto-description') {
        return 'applied extension AI Auto Description'
      } else if (info.service === 'ai-tasks') {
        return 'applied AI Tasks extension'
      }
      return 'applied extension'
    case 'file.add_tags':
      return 'added tags'
    case 'file.remove_tags':
      return 'removed tags'
    case 'file.add_ai_tags':
      return 'added AI tags'
    case 'file.remove_ai_tags':
      return 'removed AI tags'
    case 'file.rename':
      return 'renamed the file'
    case 'file.create_version':
      return `created ${info?.versionsCreated} new File version(s)`
    case 'file.version_delete':
      return 'deleted File version'
    case 'file.version_restore':
      return 'restored File version'
    case 'file.change_publish_status':
      return info.publish ? 'published the file' : 'unpublished the file'
    case 'add_public_link':
      return 'added Public link'
    case 'update_public_link':
      return 'updated Public link'
    case 'delete_public_link':
      return 'removed Public link'
    case 'media-collection.add_assets':
      return `added ${info.length} asset(s)`
    case 'media-collection.remove_assets':
      return `removed ${info.length} asset(s)`
    case 'media-collection.rename':
      return 'renamed the collection'
    case 'media-collection.update':
      return 'updated the details'
    case 'change_access_control':
      return 'changed the access for this asset'
    case 'file.created':
    case 'folder.created':
      const assetType = operation.split('.', 2)[0]
      const origin = info as HistoryAuditLogsResponse['origin']
      switch (true) {
        case origin?.reason === 'Move':
          return `moved the ${assetType} here`
        case origin?.reason === 'Copy':
          return `copied the ${assetType} here`
        default:
          return assetType === 'file'
            ? 'uploaded the file'
            : 'created the folder'
      }
    case 'media-collection.created':
      return 'created the collection'
    case 'file.delete':
    case 'folder.delete':
    case 'file.delete_by_move':
    case 'folder.delete_by_move':
    case 'media-collection.delete':
      return ''
    case 'folder.rename':
      return 'renamed the folder'
    case 'nested_asset_rename':
      return 'renamed the parent folder'
    default:
      return ''
  }
}
